import { useState } from "react";
import { ConfigProvider, Input, Modal } from "antd";
import { FileDoneOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { BillOut, CloseBillFiscalInvoiceIn } from "../../../../../api/bills";
import { useViewport } from "../../../../../hooks/useViewport";
import { useIsDarkTheme, useThemePalette } from "../../../../../store/hooks";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  PrimaryButton,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { useOrderingMessage } from "../../useOrderingMessage";
import { NoticeBanner } from "./NoticeBanner";
import { InvoiceBuyerFields } from "./InvoiceBuyerFields";
import { refusedFiscalInvoiceBuyerDraftOf } from "./refusedFiscalInvoiceBuyers";
import { useInvoiceBuyer } from "./useInvoiceBuyer";

interface Props {
  /** Отворената сметка от „Фактуриране" → „Отворени сметки — фискална фактура".
   *  Модалът се монтира само за нея (`useInvoicingFlow`) — всяко отваряне е
   *  празно, освен след отказано закриване на същата сметка. */
  bill: BillOut;
  /** Следващият номер от общата поредица (`fiscal-availability`) — само за показване. */
  nextNumber: string | null;
  /** Касата кара картов терминал (`card_terminal` на `fiscal-availability`):
   *  „Закрий" ще е само в брой — казва се ПРЕДИ купувача. */
  cardTerminal?: boolean;
  /** Фирмата издава и софтуерни фактури — бележката казва и пътя за карта
   *  (бон без фактура + софтуерна фактура към него). */
  softwareAllowed?: boolean;
  onClose: () => void;
  /** Купувачът е въведен — родителят отваря „Закрий" за сметката с него. */
  onConfirm: (bill: BillOut, fiscalInvoice: CloseBillFiscalInvoiceIn) => void;
}

/**
 * Стъпка 2 на фискалната фактура при закриване: купувачът (фирма с ЕИК) —
 * същите полета, търсене и извличане от регистъра като при издаване на
 * фактура (`useInvoiceBuyer` + `InvoiceBuyerFields`). Нищо не се записва тук:
 * купувачът пътува с „Закрий" (`fiscalInvoice` в `/close`) и BE записва
 * фактурата чак когато фискалното устройство я издаде.
 */
export const FiscalInvoiceBuyerModal = ({
  bill,
  nextNumber,
  cardTerminal = false,
  softwareAllowed = false,
  onClose,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const isDark = useIsDarkTheme();
  const themePalette = useThemePalette();
  const message = useOrderingMessage();
  // Отказано ли е закриването на сметката (отказът идва, когато модалът вече го
  // няма — `refusedFiscalInvoiceBuyers.ts`), купувачът му стои попълнен.
  const [refusedBuyer] = useState(() => refusedFiscalInvoiceBuyerDraftOf(bill.id));
  const buyer = useInvoiceBuyer({ open: true, initial: refusedBuyer });
  // Грешките светват чак след опит — иначе модалът „крещи" при отваряне.
  const [showErrors, setShowErrors] = useState(false);

  const errStatus = (key: string) =>
    showErrors && buyer.errors[key] ? ("error" as const) : undefined;

  const handleConfirm = () => {
    setShowErrors(true);
    const firstError = Object.values(buyer.errors)[0];
    if (firstError) {
      message.warning(firstError);
      return;
    }
    onConfirm(bill, {
      customer: buyer.toCustomerPayload(),
      receivedBy: buyer.receivedBy.trim() || null,
    });
  };

  const modalWidth = isMobile ? undefined : isTablet ? 520 : 560;

  return (
    // Локална antd тема (разрешеното изключение от „само Tailwind") — като в
    // `InvoiceCustomerEditModal`: полетата в pos токените вместо синьото на
    // antd; на тесен екран 16px, иначе iOS Safari зуумва при фокус.
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: themePalette.posAccent,
          colorBorder: isDark ? "#4d4437" : "#E5DED3", // pos-ctl (elevated — модал)
          borderRadius: 12,
          fontSize: isNarrow ? 16 : 14,
        },
      }}
    >
      <Modal
        open
        onCancel={onClose}
        footer={null}
        centered
        closable={false}
        destroyOnHidden
        width={modalWidth}
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div className="flex max-h-[92vh] flex-col bg-pos-card">
          <ModalHeader
            icon={<FileDoneOutlined />}
            title={t("fiscalInvoice.buyer.title", { table: bill.name })}
            subtitle={t("fiscalInvoice.buyer.subtitle", {
              amount: (parseFloat(bill.total_amount) || 0).toFixed(2),
            })}
            onClose={onClose}
            isNarrow={isNarrow}
          />

          <div
            className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-pos-canvas ${
              isNarrow ? "px-4 py-3.5" : "px-6 py-[18px]"
            }`}
          >
            {/* Картовият терминал — преди купувача, за да не се въвежда напразно
                за гост, който ще плати с карта. */}
            {cardTerminal && (
              <NoticeBanner
                tone="warn"
                text={t(
                  softwareAllowed
                    ? "fiscalInvoice.buyer.cardTerminalSoftware"
                    : "fiscalInvoice.buyer.cardTerminal",
                )}
              />
            )}

            <NoticeBanner
              tone="info"
              text={
                nextNumber
                  ? t("fiscalInvoice.buyer.numberNext", { number: nextNumber })
                  : t("fiscalInvoice.buyer.numberFromDevice")
              }
            />

            <InvoiceBuyerFields
              buyer={buyer}
              errStatus={errStatus}
              layout="form"
              onPressEnter={handleConfirm}
              autoFocusSearch={isDesktop}
            />

            <div className="flex flex-col gap-[5px]">
              <span className="text-[11px] font-semibold tracking-[.2px] text-pos-soft">
                {t("invoiceCreate.fieldReceivedBy")}
              </span>
              <Input
                value={buyer.receivedBy}
                maxLength={255}
                placeholder={t("invoiceCreate.fieldReceivedByPh")}
                onChange={(e) => buyer.setReceivedBy(e.target.value)}
                onPressEnter={handleConfirm}
                allowClear
              />
            </div>
          </div>

          <ModalFooter isNarrow={isNarrow} narrowFirst>
            <GhostButton
              label={t("common.cancel")}
              onClick={onClose}
              shortcut={isDesktop ? "Esc" : undefined}
            />
            <PrimaryButton
              label={t("fiscalInvoice.buyer.toPayment")}
              onClick={handleConfirm}
              shortcut={isDesktop ? "⏎" : undefined}
            />
          </ModalFooter>
        </div>
      </Modal>
    </ConfigProvider>
  );
};
